import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { Role } from '../api/types';
import { roleLabel } from '../roles';
import { useAuth } from '../auth';
import { BrandLogo } from '../components/Layout';
import { Field, PasswordInput } from '../components/ui';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useI18n } from '../i18n';

const REMEMBER_KEY = 'survey.remember';

function readRemember(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) !== '0';
  } catch {
    return true;
  }
}

function safeNext(next: string | null): string {
  // Only allow in-app relative paths to prevent open redirects.
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export function LoginPage() {
  const { login, user } = useAuth();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const next = safeNext(params.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(readRemember);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);

  useEffect(() => {
    if (user) nav(next, { replace: true });
  }, [user, next, nav]);
  useEffect(() => {
    api.config().then((c) => setAllowSignup(c.allowSignup), () => {});
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      try {
        localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
      } catch {
        // storage unavailable; the choice just isn't remembered
      }
      await login(email, password, remember);
      nav(next, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.signin_failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="corner-switch">
        <LanguageSwitcher />
      </div>
      <form className="card auth-card" onSubmit={submit}>
        <BrandLogo height={30} />
        <h1>{t('auth.welcome_back')}</h1>
        <p className="sub">{t('auth.signin_sub')}</p>
        <div className="form-grid">
          {error && <div className="banner error">{error}</div>}
          <Field label={t('auth.email')} htmlFor="email">
            <input id="email" className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label={t('auth.password')} htmlFor="password">
            <PasswordInput id="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <label className="check" title={t('auth.remember_hint')}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span>{t('auth.remember_me')}</span>
          </label>
          <button className="btn primary" disabled={busy} style={{ height: 40 }}>
            {busy ? t('auth.signing_in') : t('auth.sign_in')}
          </button>
          {allowSignup && (
            <p className="small muted" style={{ textAlign: 'center' }}>
              {t('auth.no_account')} <Link to={`/register?next=${encodeURIComponent(next)}`}>{t('auth.create_one')}</Link>
            </p>
          )}
        </div>
      </form>
    </div>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const next = safeNext(params.get('next'));
  const invite = params.get('invite');
  // Invitation links: undefined while checking, null when the link can't be used.
  const [invitedRole, setInvitedRole] = useState<{ role: Role; roleName: string } | null | undefined>(undefined);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (invite) api.publicInvitation(invite).then((r) => setInvitedRole(r), () => setInvitedRole(null));
  }, [invite]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFields({});
    try {
      if (invite) {
        await api.registerInvited(form.email, form.name, form.password, invite);
        setSent(true);
        return;
      }
      await register(form.email, form.name, form.password);
      nav(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setFields(err.fields);
        setError(err.message);
      } else setError(t('auth.register_failed'));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="auth-page">
        <div className="corner-switch">
          <LanguageSwitcher />
        </div>
        <div className="card auth-card">
          <BrandLogo height={30} />
          <h1>{t('auth.signup_sent_title')}</h1>
          <p className="sub">{t('auth.signup_sent_body')}</p>
          <Link className="btn primary" to="/login" style={{ height: 40 }}>
            {t('auth.back_to_signin')}
          </Link>
        </div>
      </div>
    );
  }

  const inviteBlocked = !!invite && !invitedRole;
  return (
    <div className="auth-page">
      <div className="corner-switch">
        <LanguageSwitcher />
      </div>
      <form className="card auth-card" onSubmit={submit}>
        <BrandLogo height={30} />
        <h1>{t('auth.create_title')}</h1>
        <p className="sub">
          {!invite
            ? t('auth.create_sub')
            : invitedRole === undefined
              ? t('auth.invite_checking')
              : invitedRole
                ? t('auth.invite_sub', { role: roleLabel(t, invitedRole.role, invitedRole.roleName) })
                : null}
        </p>
        <div className="form-grid">
          {invite && invitedRole === null && <div className="banner error">{t('auth.invite_invalid')}</div>}
          {error && !Object.keys(fields).length && <div className="banner error">{error}</div>}
          <Field label={t('auth.name')} htmlFor="name">
            <input id="name" className="input" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label={t('auth.email')} htmlFor="email" error={fields.email}>
            <input id="email" className="input" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label={t('auth.password')} htmlFor="password" error={fields.password} hint={t('auth.password_hint')}>
            <PasswordInput id="password" autoComplete="new-password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <button className="btn primary" disabled={busy || inviteBlocked} style={{ height: 40 }}>
            {busy ? t('auth.creating') : t('auth.create_account')}
          </button>
          <p className="small muted" style={{ textAlign: 'center' }}>
            {t('auth.already_registered')} <Link to={`/login?next=${encodeURIComponent(next)}`}>{t('auth.sign_in')}</Link>
          </p>
        </div>
      </form>
    </div>
  );
}
